import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CircleDollarSign, Zap, HeartHandshake, TrendingUp } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { IMAGES } from "@/config";

const LIFECYCLE = [
  {
    num: "01",
    title: "ChurnSense AI",
    flow: "Predict → Prioritize → Intervene",
    points: ["Earlier identification of churn risk", "Better prioritization of customers", "More targeted retention actions"],
    img: IMAGES.lines,
  },
  {
    num: "02",
    title: "RetainAI",
    flow: "Identify → Understand → Personalize → Retain",
    points: ["Targeted retention, not blanket incentives", "Improved retention efficiency", "Customer lifetime value protected"],
    img: IMAGES.curves,
  },
  {
    num: "03",
    title: "WinBack AI",
    flow: "Churned → Understand Why → Personalize → Reactivate",
    points: ["Recover previously lost customers", "Personalized win-back campaigns", "Improved reactivation efficiency"],
    img: IMAGES.tunnel,
  },
];

const PILLARS = [
  { icon: CircleDollarSign, title: "Cost Optimization", points: ["Automates repetitive interactions", "24×7 service & parallel call handling"] },
  { icon: Zap, title: "Agent Productivity", points: ["Real-time transcription, translation & summary", "Automated action items and live guidance"] },
  { icon: HeartHandshake, title: "Customer Experience", points: ["Faster, consistent, multilingual responses", "Personalized handling of frustrated customers"] },
  { icon: TrendingUp, title: "Revenue & Customer Value", points: ["Lower churn, stronger retention & win-back", "Better lifetime value per customer"] },
];

const MARKET = [
  { label: "TAM", desc: "Global spend on AI for customer interaction, intelligence, churn & retention", value: "~$22–25B" },
  { label: "SAM", desc: "North America subscription, membership, loyalty & high-volume servicing businesses", value: "~$8–10B" },
  { label: "SOM", desc: "Near-term obtainable across existing client footprint and membership programs", value: "~$120–200M" },
];

export default function Approach() {
  return (
    <main data-testid="use-cases-page">
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-void via-void/75 to-void/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/60" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
        <Reveal y={20}>
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Use Cases</p>
        </Reveal>
        <h1 className="mt-8 font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-bone max-w-5xl">
          <MaskedLine delay={0.15}>A connected response to</MaskedLine>
          <MaskedLine delay={0.3}>
            customer <span className="italic text-vermilion">risk.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-10 max-w-xl text-ash text-base md:text-lg leading-relaxed">
            From prediction, through personalized intervention, to recovery — one lifecycle, three linked engines.
          </p>
        </Reveal>
        </div>
      </header>

      {/* LIFECYCLE */}
      <section data-testid="approach-lifecycle-section" className="border-t border-line py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-bone leading-[1.02]">
              Churn <span className="text-vermilion">→</span> Retention <span className="text-vermilion">→</span> <span className="italic">Win-Back</span>
            </h2>
          </Reveal>
          <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-px bg-line border border-line">
            {LIFECYCLE.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.12} className="bg-void">
                <div data-testid={`lifecycle-step-${step.num}`} className="group relative h-full border border-line lg:border-0 overflow-hidden">
                  <div className="relative h-40 md:h-48 overflow-hidden">
                    <img
                      src={step.img}
                      alt={`${step.title} abstract visual`}
                      className="w-full h-full object-cover opacity-50 transition-all duration-700 group-hover:opacity-75 group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
                    <span className="absolute bottom-4 left-8 md:left-12 font-mono-tech text-sm text-bone">{step.num}</span>
                  </div>
                  <div className="p-8 md:p-12">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-tech text-sm text-vermilion">{step.num}</span>
                    {i < 2 && <ArrowRight size={18} strokeWidth={1} className="text-ash group-hover:text-vermilion group-hover:translate-x-1 transition-all duration-500" />}
                  </div>
                  <h3 className="mt-10 font-display text-3xl md:text-4xl text-bone">{step.title}</h3>
                  <p className="mt-4 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-vermilion leading-relaxed">{step.flow}</p>
                  <div className="mt-8 h-px w-full bg-line" />
                  <ul className="mt-8 space-y-3">
                    {step.points.map((pt) => (
                      <li key={pt} className="text-ash text-sm leading-relaxed border-l border-line pl-4">{pt}</li>
                    ))}
                  </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PILLARS */}
      <section data-testid="approach-pillars-section" className="relative border-t border-line py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={IMAGES.approach} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-void via-void/50 to-void" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Four value pillars</p>
            <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tight text-bone max-w-3xl leading-[1.02]">
              Where the value <span className="italic">lands.</span>
            </h2>
          </Reveal>
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {PILLARS.map((pillar, i) => (
              <Reveal key={pillar.title} delay={i * 0.1} className="bg-void/90 backdrop-blur-sm">
                <div data-testid={`pillar-${i}`} className="group p-8 md:p-10 h-full">
                  <pillar.icon size={26} strokeWidth={1} className="text-ash group-hover:text-vermilion transition-colors duration-500" />
                  <h3 className="mt-8 font-display text-2xl md:text-3xl text-bone leading-tight">{pillar.title}</h3>
                  <ul className="mt-6 space-y-3">
                    {pillar.points.map((pt) => (
                      <li key={pt} className="text-ash text-sm leading-relaxed border-l border-line pl-4">{pt}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET */}
      <section data-testid="approach-market-section" className="border-t border-line py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">The opportunity</p>
            <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tight text-bone max-w-3xl leading-[1.02]">
              A market measured in <span className="italic text-vermilion">billions.</span>
            </h2>
          </Reveal>
          <div className="mt-20">
            {MARKET.map((m, i) => (
              <Reveal key={m.label} delay={i * 0.1}>
                <div data-testid={`market-row-${m.label.toLowerCase()}`} className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-12 py-10 md:py-14 border-t border-line items-baseline">
                  <span className="md:col-span-2 font-mono-tech text-sm text-vermilion">{m.label}</span>
                  <p className="md:col-span-6 text-ash text-sm md:text-base leading-relaxed max-w-xl">{m.desc}</p>
                  <p className="md:col-span-4 font-mono-tech text-3xl md:text-5xl text-bone md:text-right group-hover:text-vermilion transition-colors duration-500">{m.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-ash">
              2026 estimates · illustrative, subject to pilot measurement
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line py-32 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-bone max-w-3xl leading-[1.02]">
              Bring the layer to <span className="italic text-vermilion">your operation.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="approach-cta-demo"
              className="mt-10 inline-flex items-center gap-3 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98] group"
            >
              Request a demo
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
